<template>
  <AuthCard
    title="Create your business account"
    subtitle="Just your email and a password for now. Your business details come next and take about two minutes."
  >
    <v-form ref="formRef" v-model="formValid" @submit.prevent="onSubmit">
      <v-text-field
        v-model="email"
        label="Email"
        type="email"
        autocomplete="email"
        :rules="[rules.required, rules.email]"
        :disabled="loading"
        variant="outlined"
        density="comfortable"
        class="mb-2"
        @input="onEmailInput"
      />

      <v-text-field
        v-model="password"
        label="Password"
        :type="showPassword ? 'text' : 'password'"
        autocomplete="new-password"
        :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
        @click:append-inner="showPassword = !showPassword"
        :rules="[rules.password]"
        :disabled="loading"
        variant="outlined"
        density="comfortable"
        class="mb-2"
        hint="At least 8 characters with an uppercase, lowercase and number."
        persistent-hint
      />

      <v-text-field
        ref="confirmPasswordRef"
        v-model="confirmPassword"
        label="Confirm password"
        :type="showConfirm ? 'text' : 'password'"
        autocomplete="new-password"
        :append-inner-icon="showConfirm ? 'mdi-eye-off' : 'mdi-eye'"
        @click:append-inner="showConfirm = !showConfirm"
        :rules="[rules.required, matchesPassword]"
        :disabled="loading"
        variant="outlined"
        density="comfortable"
        class="mb-4"
      />

      <v-alert
        v-if="errorMessage"
        type="error"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        {{ errorMessage }}
      </v-alert>

      <v-btn
        type="submit"
        block
        size="large"
        :loading="loading"
        :disabled="loading"
        class="submit-btn"
      >
        Create account
      </v-btn>
    </v-form>

    <template #footer>
      Already have a business account?
      <router-link to="/business-signin">Sign in</router-link>
    </template>
  </AuthCard>
</template>

<script>
import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import AuthCard from "@/components/Molecules/AuthCard.vue";
import { signUpOwner } from "@/stores/authStore.js";
import { track } from "@/analytics.js";
import { passwordRules as rules } from "@/utils/passwordRules.js";

export default {
  name: "BusinessSignUpComponent",
  components: { AuthCard },
  setup() {
    const router = useRouter();
    const formRef = ref(null);
    const confirmPasswordRef = ref(null);
    const formValid = ref(false);
    const email = ref("");
    const password = ref("");
    const confirmPassword = ref("");
    const showPassword = ref(false);
    const showConfirm = ref(false);
    const loading = ref(false);
    const errorMessage = ref("");

    const matchesPassword = (v) =>
      v === password.value || "Passwords do not match";

    // Re-run the confirm-password rule when the password field changes so the
    // mismatch hint clears (or re-appears) without waiting for a blur.
    watch(password, () => {
      if (confirmPassword.value) {
        confirmPasswordRef.value?.validate();
      }
    });

    const onEmailInput = () => {
      email.value = email.value.replace(/[^a-zA-Z0-9._%+\-@]/g, "").toLowerCase();
    };

    const onSubmit = async () => {
      errorMessage.value = "";
      const validation = await formRef.value?.validate();
      if (!validation?.valid) return;

      loading.value = true;
      try {
        await signUpOwner(email.value, password.value);
        track("business_signup_succeeded", {});
        router.push("/register-business");
      } catch (error) {
        errorMessage.value =
          error?.message || "Something went wrong. Please try again.";
        track("business_signup_failed", { error_message: errorMessage.value });
      } finally {
        loading.value = false;
      }
    };

    return {
      formRef,
      confirmPasswordRef,
      formValid,
      email,
      password,
      confirmPassword,
      showPassword,
      showConfirm,
      loading,
      errorMessage,
      rules,
      matchesPassword,
      onEmailInput,
      onSubmit,
    };
  },
};
</script>

<style scoped>
.submit-btn {
  background: rgb(0, 109, 62) !important;
  color: #ffffff !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0;
  border: none !important;
  border-radius: 999px !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.submit-btn:hover {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
  transform: translateY(-1px);
}
</style>
